import React, { useRef } from "react";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { RCCInput, RCCHookToast, RCCGrid, RCCMicrosoftSocialLoginButton, RCCRefMicrosoftSocialLoginButton, RCCUseMicrosoftSocialLogin, RCCServicesApi } from "react-common-components";
import { useForm } from "react-hook-form";
import {getValidationErrors, standardErrorMessages} from "src/utils/errors";
import { useTranslation } from "src/context/Translations/context";
import LoginScreen from "src/components/LoginScreen";
import { useAuth } from "src/context/Auth/context";
import getMicrosoftDomainOfEmail from "src/context/MicrosoftSocialLogin/utils";

const LoginMicrosoft: React.FC = () => {
	const { translate } = useTranslation();
	const { register, setError, clearErrors, handleSubmit, formState: { errors } } = useForm();
	const { addToast } = RCCHookToast();
	const { updateMsalInstance } = RCCUseMicrosoftSocialLogin();
	const {signIn} = useAuth();
	const { REACT_APP_ADDRESS } = process.env;
	const refMicrosoftSocialLoginButton = useRef<RCCRefMicrosoftSocialLoginButton>(null);

	const handleLogin = async () => {
		if(!refMicrosoftSocialLoginButton.current)
			return;

		try{
			const dt = await refMicrosoftSocialLoginButton.current.handleLogin();
				
			if(!dt.account)
				throw new Error();
			
			await RCCServicesApi.postRequest({
				method: "UserPublic/MicrosoftSocialLoginValidation",
				body: {
					email: dt.account.username,
					password: dt.account.localAccountId
				}
			});

			await signIn({
				email: dt.account.username,
			    password: dt.account.localAccountId,
				remember: true,
				executeBeforeTFA: () => refMicrosoftSocialLoginButton?.current?.resetLoading()
			});
		}
		catch(_){
			refMicrosoftSocialLoginButton?.current?.resetLoading();
			addToast({
				color: "red",
				description: translate("Erro ao efetuar login neste domínio"),
				title: translate("Erro")
			});
		}
	};

	const submitForm = async (data: {
		email: string;
	}) :Promise<void> => {
		try
		{
			clearErrors();
			refMicrosoftSocialLoginButton?.current?.setLoading();

			const schema = Yup.object().shape({
				email: Yup.string().email(translate("Email inválido")).required(translate("Email é obrigatório"))
			});

			await schema.validate(data, {
				abortEarly: false
			});

			const configs = await getMicrosoftDomainOfEmail(data.email, translate, addToast);
			if(!configs){
				refMicrosoftSocialLoginButton?.current?.resetLoading();
				return;
			}
			await updateMsalInstance(configs.clientId, configs.cloudInstance, configs.tenantInfo, REACT_APP_ADDRESS);

			await handleLogin();
		}
		catch(err)
		{
			refMicrosoftSocialLoginButton?.current?.resetLoading();
			if(getValidationErrors(err, setError))
				return;

			standardErrorMessages(err, translate("Verifique os campos e tente novamente"), addToast);
		}
	};

	return (
		<LoginScreen contentPosition="start">
			<form onSubmit={handleSubmit(submitForm)}>
				<h1>{translate("Entrar com a Microsoft")}</h1>
				<RCCInput 
					name="email"
					type="email" 
					label={translate("Email")}
					register={register} 
					error={errors.email}
					layout="outlined"
					icon={{
						name: "user",
						color: "var(--purple-lighter)"
					}}
				/>
				<br />
				<RCCMicrosoftSocialLoginButton
					ref={refMicrosoftSocialLoginButton} 
					onClick={handleSubmit(submitForm)}
					styleInline={`
						padding: 24px;
						width: 100%;
						>span.span-of-component-button{
							font-size: 1rem;
							font-weight: bold;
						}	
					`}
				>
					{translate("Entrar com a Microsoft")}
				</RCCMicrosoftSocialLoginButton>
				<br />
				<RCCGrid container item justifyContent="center">
					<Link to="/login">{translate("Voltar")}</Link>
				</RCCGrid>
			</form>

		</LoginScreen>
	);
};

export default LoginMicrosoft;
